import {isSecondaryManual} from './componentManualLauncherAssembly'
// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// NEON NOVA in three dimensions (M3-03, M3-04, M3-05 flippers, M3-08).
//
// A `SceneBuilder` for `SceneHost`: builds the cabinet, the playfield and the moving parts from
// the machine definition and the live `World`, and poses them once per frame at the driver's
// interpolation alpha.
//
// **It reads the simulation and never writes to it.** `update(alpha)` interpolates between
// `prevX`/`x` and `prevAngle`/`angle` — the two states the fixed step already produced — and
// that is the whole of its relationship with the physics. There is no integration here, no
// `deltaTime`, and nothing this file does can change where a ball goes. A phone at 30 fps and a
// desktop at 144 see different numbers of pictures of the identical game (PH-022).
//
// The playfield's own frame is PH-001: +X right, +Y up the playfield, +Z out of the surface. It
// is a child group of the cabinet, rotated by the incline, so every coordinate in the machine
// definition and every ball position out of the solver can be used *as written*. Converting
// coordinates at each use is how a renderer and a simulation quietly stop agreeing.

import {
  Box3,
  BoxGeometry,
  BufferGeometry,
  Float32BufferAttribute,
  Color,
  DirectionalLight,
  Group,
  HemisphereLight,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  PointLight,
  Vector2,
  Vector3,
  type Material,
  type Object3D,
  type Scene,
  type Texture,
} from 'three'
import type { LoadedMachine, MachineDevice } from '@core/machine'
import {raisedDeckInserts} from './raisedDeckInserts'
import {navAcrylicSlab} from './navAcrylicSlab'
import { buildCabinet, cabinetDimensionsFor, bodyLengthFor, cabinetBottomY } from './cabinet'
import { registerCabinetFloor } from './cabinetFloor'
import {
  buildDeviceMesh,
  buildDevices,
  runtimeFor,
  type BuiltDevice,
  type DeviceAnimator,
  type DeviceRuntime,
} from './deviceMeshes'
import { authoredHardwareFor } from './authoredHardware'
import { lowerAssemblyMatches, withAuthoredLowerAssembly } from './authoredLowerAssembly'
import { withSlingAssembly } from './slingAssembly'
import { withAuthoredFlippers } from './authoredFlippers'
import { withAuthoredSpinners } from './authoredSpinners'
import { postPlacementKey } from './authoredPosts'
import { buildRails, type BuiltRails } from './rails'
import { buildRamps } from './ramps'
import { buildBalls,createBallMaterials, type BuiltBalls } from './balls'
import { buildDmdPanel, type BuiltDmdPanel } from './dmdPanel'
import {
  buildLamps,
  LAMP_FRAGMENT_HOOK,
  LAMP_UNIFORM_DECLARATIONS,
  type BuiltLamps,
  type LampStateReader,
} from './lamps'
import { buildGi, GI_FRAGMENT_HOOK, GI_UNIFORM_DECLARATIONS, type BuiltGi } from './generalIllumination'
import {INSERT_OPTICS_DECLARATIONS,INSERT_OPTICS_ROUGHNESS,INSERT_OPTICS_NORMAL,INSERT_OPTICS_LIGHT,type InsertOpticsUniforms} from './insertOpticsShader'
import { buildFlashers, type BuiltFlashers } from './flashers'
import { buildComponentLights, type BuiltComponentLights } from './componentLights'
import type { ComponentLightingState } from './componentLightingState'
import type { BackglassSurface } from './cabinet'
import { buildToys, type BuiltToys, type ToyStateReader } from './toys'
import { buildGlass } from './glass'
import { buildLightSpill, type BuiltSpill } from './lightSpill'
import { NEUTRAL_MATCH, type LightingMatch } from '../xr/lightingMatch'
import { findDmdWindow } from './dmdPlacement'
import type { DmdRaster } from '../fx/dmd'
import { FINISHES, PartPalette, UNPAINTED_PLAYFIELD } from './partsKit'
import { addAuthoredDisplayFrame } from './authoredDisplays'
import { placePanel } from './dmdPlacement'
import { buildSnapGuides, type SnapGuidesBuild } from './snapGuides'
import type { SnapView } from '../xr/build/snapView'
import { buildValidationOverlay, type ValidationOverlayBuild } from './validationOverlay'
import type { ValidationView } from '../xr/build/validationView'
import { buildInspectorCard, type InspectorCardBuild } from './inspectorCard'
import type { InspectorView } from '../xr/build/inspector'
import { buildMiniMap, type MiniMapBuild } from './miniMap'
import type { MiniMapView } from '../xr/build/miniMap'
import { buildRampHandles, type RampHandlesBuild } from './rampHandles'
import { buildBallRibbon, legendAt, type BallRibbonBuild } from './ballRibbon'
import type { RibbonLegendRow } from '../xr/build/testTools'
import type { BallTrail } from '../engine/ballTrail'
import type { PointsView } from '../xr/build/rampPoints'
import { renderArtTexture } from './artTexture'
import { packagedIllustration } from './packagedIllustrations'
import { buildInstructionCards } from './instructionCards'
import { scoopDeck,scoopDeckKey } from './scoopDeck'
import printedBoardFinish from '../../SourceArt/PierGlazing/printed-board-finish-04.json'
import { attachContactCoating,refreshContactCoating } from './contactCoating'
import reflectionFinish from '../../SourceArt/PierContact/reflection-finish.json'
import { TIER_BUDGETS, type RenderTier } from './tiers'
import type { SceneBuilder } from './SceneHost'
import { environmentPanelsFor } from './environment'
import { lightingProfileFor } from './lightingProfiles'
import type { ArtSurface } from '../fx/artProgram'

/** One device as `MachineScene` drew it (M13-04). */
interface DrawnDevice {
  readonly definition: MachineDevice
  readonly group: Group | undefined
  readonly geometries: readonly BufferGeometry[]
  readonly animator: DeviceAnimator | undefined
  readonly skipped: string | undefined
  /** Point its animator at another world's copy. See `BuiltDevice.rebind`. */
  readonly rebind: (runtime: DeviceRuntime) => void
}

/** What `MachineScene.patch` did, by device id. */
export interface PatchReport {
  readonly added: readonly string[]
  readonly removed: readonly string[]
  readonly changed: readonly string[]
  /**
   * The world was a different object, so every untouched device's animator and the balls were
   * pointed at the new one (M13-10). False when only the definition moved.
   */
  readonly rebound: boolean
}

/** The ghost Build mode draws where a carried part would land (M13-10). */
/**
 * A placed part riding the hand off the board is drawn over everything, the ghost hands included
 * (24 Oct 2026): at the pinch point, behind the drawn fingers, a part at the bench's scale was not
 * seen at all between the board's edge and the remove tray. On the board it is in the machine, an
 * ordinary depth test, so a part behind a ramp reads as behind it.
 */
const GHOST_ON_TOP_ORDER = 1001

function paintGhostOnTop(drawn: DrawnGhost, onTop: boolean): void {
  for (const material of drawn.materials) {
    if (material.depthTest === !onTop) continue
    material.depthTest = !onTop
    material.needsUpdate = true
  }
  drawn.group.traverse((node) => {
    if ((node as Mesh).isMesh) node.renderOrder = onTop ? GHOST_ON_TOP_ORDER : 0
  })
}

/** How fast a dropped part falls, m/s² — slower than the room's gravity so the fall reads. */
export const DROP_GRAVITY = 4
/** How long a dropped part rests on the box's floor, then how long it takes to fade, seconds. */
export const DROP_REST_SECONDS = 1
export const DROP_FADE_SECONDS = 0.5

interface Dropped {
  readonly body: Object3D
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly MeshBasicMaterial[]
  readonly opacity: number
  readonly from: Vector3
  readonly to: Vector3
  age: number
}

interface DrawnGhost {
  readonly device: MachineDevice | import('../builder/parts').FlasherPart
  readonly group: Group
  /** Where the build put the group, before any `shift` (a placed part riding the hand, 24 Oct 2026). */
  readonly base?: readonly [number, number, number]
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly Material[]
}

/** How see-through the ghost is. Enough to read the surface under it, enough to see the part. The `ghost` finish's (M13-18). */
export const GHOST_OPACITY = FINISHES.ghost.opacity ?? 0.45
/** And its colour: the same pale blue on both clients, rather than the part's own colours faded. */
export const GHOST_COLOR = FINISHES.ghost.color

/**
 * The hover halo: a copy of the placed part under the fingertip, grown a little and lit.
 *
 * **A part the author could pick up has to look like one** (M13-13's first half; the second
 * device day of M13). Moving a placed part was in M13-07 from the start — pinch on its handle and
 * carry — and the author who used the mode for an hour said *"it would be nice if the user can
 * move the components"*, because nothing ever said a part was under the finger. The halo says
 * it: the same mesh the part is drawn with, at `HALO_SCALE`, in `HALO_COLOR`, read through the
 * glass and the part itself so it is visible from where the author is looking.
 */
export const HALO_SCALE = 1.12
/** The `selected` finish's (M13-18): the same cyan at the same opacity on both clients. */
export const HALO_OPACITY = FINISHES.selected.opacity ?? 0.4
export const HALO_COLOR = FINISHES.selected.color

export class MachineScene implements SceneBuilder {
  get environmentPanels() {
    return environmentPanelsFor(lightingProfileFor(this.definition.machineId).environment)
  }
  /**
   * The playfield-local group: PH-001 axes, at the playfield's place in the world.
   *
   * **Not** the machine frame. A ball's `x`/`y` are absolute — the solver queries static
   * geometry at the ball's position *minus* the nudge displacement — so this group is the
   * frame a ball is drawn in, and it never moves.
   */
  /**
   * Everything that is *the machine*, in one group (M5).
   *
   * The cabinet, the playfield and the machine's own lighting rig hang off this, and the scene
   * holds only this. It exists because XR has to be able to put the machine somewhere — on a real
   * table at 0.42 scale, or in a bay in the arcade — and moving one group is the difference
   * between placing a machine and rebuilding one. On a flat screen it sits at the origin,
   * unrotated and unscaled, and costs one matrix multiply a frame.
   *
   * The lights are inside it deliberately: they are the *machine's* lighting, positioned in
   * metres relative to the cabinet, and a rig left behind in room space would light empty floor
   * while a tabletop machine sat in the dark.
   */
  readonly root = new Group()

  readonly playfield = new Group()

  /**
   * The machine frame, inside the playfield group: everything a nudge moves.
   *
   * Nudging shoves the cabinet, not the ball. The solver models exactly that, and the 2-D
   * collision view has always drawn it — but the 3-D scene drew the machine bolted in place
   * and the balls in absolute space, so a nudge made the *balls* jump sideways. Relatively
   * correct and physically backwards, and it reads as the shove hitting the ball directly,
   * which is precisely the thing PH-A26 exists to say a nudge does not do.
   */
  readonly frame = new Group()

  /**
   * The cabinet's own nudge offset, in world space.
   *
   * Separate from `frame` because the cabinet is built in world axes rather than in playfield
   * axes, so the same in-plane displacement has to be rotated onto the incline to move the
   * body and the playfield together. They must move together: a cabinet that stayed put while
   * its own playfield slid inside it would be a far stranger picture than no nudge at all.
   */
  readonly cabinetFrame = new Group()

  /** Cached from the definition, so the per-frame cabinet offset is two multiplications. */
  private inclineSin = 0
  private inclineCos = 1

  /**
   * The machine's own lights, with the intensity each was built at (M9-51).
   *
   * **The base, not the last value.** `matchRoom` scales from these every time it is called, so a
   * scale applied to its own output cannot compound — which is what a room getting one frame
   * darker for every frame of a dim estimate would be, and it ends with the machine unlit.
   */
  private roomLit: { light: { intensity: number }; base: number }[] = []

  /**
   * The body's paint and the colour it was painted, so a tint is applied to the paint (M9-51).
   *
   * Same argument as `roomLit`: the tint is a multiplication, and a multiplication applied to its
   * own result is a cabinet whose colour drifts for as long as the player stands in a coloured
   * room. Cloned, because `material.color` is the live object the renderer reads.
   */
  private painted: { material: MeshStandardMaterial; base: Color }[] = []

  /** The pool of light on the floor. Built with the machine and hidden until there is one. */
  private spill: BuiltSpill | undefined

  /**
   * Read through a provider, not captured once.
   *
   * Rebuilding a machine replaces its `World` wholesale, and a scene holding the old one would
   * pose its meshes from a simulation nobody is stepping — a machine that looks frozen while
   * the game underneath it plays perfectly. The provider costs a property read per frame and
   * removes the whole class of mistake.
   */
  private readonly machine: () => LoadedMachine
  /**
   * The display's current raster, or nothing.
   *
   * A provider, like the machine itself, and for a stronger reason: the scene must never own
   * the display. A DMD is driven by the *game* — a jackpot animation is a rule firing, not a
   * rendering decision — so the scene reads whatever the game's display driver has produced
   * and uploads it. A scene that owned a `DmdPlayer` would be a second machine's worth of
   * display state, drifting away from the one the player is scoring on.
   */
  private readonly dmdRaster: () => DmdRaster | undefined
  /**
   * How bright each lamp is, read from the rule engine (M3-15).
   *
   * A provider for the same reason the machine is one, and a *reader* rather than a snapshot
   * because 43 lamps queried once a frame is 43 map lookups and copying them first would be
   * 43 map lookups plus an allocation. A machine with no lamp source lights nothing, which is
   * what an attract-mode preview of a machine nobody is playing should look like.
   */
  private readonly lampState: () => LampStateReader | undefined
  /**
   * The rule variables and running modes a toy displays (M3-07).
   *
   * A separate reader from `lampState` because it answers a different question. A lamp is a
   * bulb and a toy is a **state display**: the star's appearance is the Star Core charge, not a
   * lamp somebody remembered to wire to it. Squeezing it through the lamp reader would mean the
   * rulesheet had to keep a lamp in step with a variable, which is a second copy of the truth.
   */
  private readonly toyState: () => ToyStateReader | undefined
  /** The player's motion budget, 0…1. Read each frame; the preference can change mid-game. */
  private readonly motionScale: () => number
  /**
   * Is the player asking for less flashing (NFR-026)?
   *
   * A separate question from the motion budget, and deliberately not derived from it. They
   * overlap and they come apart in both directions: a photosensitive player may be perfectly
   * happy with a panning camera, and somebody who gets motion sick may want the machine to flash
   * exactly as a real one does. Read per frame, because the answer can change while a game runs.
   */
  private readonly flashReduced: () => boolean
  private world: LoadedMachine['world']
  private definition: LoadedMachine['definition']

  private readonly geometries: BufferGeometry[] = []
  private readonly materials: Material[] = []
  private readonly textures: Texture[] = []
  private balls: BuiltBalls | undefined

  /** Materials shared by every rail, post and mechanism. Owned here, disposed here. */
  private palette = new PartPalette()
  private animators: readonly DeviceAnimator[] = []

  /**
   * The devices as drawn, one entry per definition id (M13-04).
   *
   * Kept beside the geometry rather than folded into it, because `patch` needs to answer "is
   * the device I drew still the device the definition has?" by *identity* — the Builder's draft
   * is immutable and an untouched device keeps its object, so a moved slingshot rebuilds one
   * group and twenty-seven neighbours keep theirs. Device geometries live here and not in
   * `geometries`, so one device can be disposed on its own.
   */
  private readonly drawn = new Map<string, DrawnDevice>()
  /** The placed part in hand, whose drawn copy is hidden (`lift`). */
  private lifted: string | undefined
  /** The group the drawn devices hang off. `undefined` until `build`. */
  private devicesGroup: Group | undefined
  /** Build mode's snap guides, built on the first frame that has a snap to show (M13-13). */
  private guidesDrawn: SnapGuidesBuild | undefined
  /** Build mode's validation overlay, built on the first frame that has a report to show (M13-17). */
  private validationDrawn: ValidationOverlayBuild | undefined
  /** Build mode's inspector card, built on the first frame that has a selection to show (M13-22). */
  private inspectorDrawn: InspectorCardBuild | undefined
  /** Build mode's mini-map, built on the first frame of editing (M13-23). */
  private miniMapDrawn: MiniMapBuild | undefined
  private rampHandlesDrawn: RampHandlesBuild | undefined
  private ribbonDrawn: BallRibbonBuild | undefined
  /** Build mode's ghost, while a part is over the board (M13-10). */
  private ghostDrawn: DrawnGhost | undefined
  /** Parts let go over the remove tray, falling into it, resting, fading (24 Oct 2026). */
  private readonly drops: Dropped[] = []
  /** Build mode's hover halo, while a fingertip is over a placed part's handle (M13-13). */
  private haloDrawn: (DrawnGhost & { readonly id: string }) | undefined
  /** What the balls were built with, so `patch` can rebuild them for a new world. */
  private ballMaterials: readonly Material[] | undefined
  private ballSegments = 32
  private dmd: BuiltDmdPanel | undefined
  private lamps: BuiltLamps | undefined
  /** The playfield material the inserts are lit on, for `patch` to hand a new insert map to. */
  private surfaceMesh:Mesh|undefined
  private surfaceKey=''
  private lampMaterial: MeshStandardMaterial | undefined
  /** The static rails (colliders), kept apart so `patch` can draw them again when a move carried one. */
  private railsDrawn: BuiltRails | undefined
  private gi: BuiltGi | undefined
  private flashers: BuiltFlashers | undefined
  private flasherWithLight=false
  private componentLights: BuiltComponentLights | undefined
  private lightingBackglass: BackglassSurface | undefined
  private toys: BuiltToys | undefined

  /**
   * Parts of the machine this build drew as nothing, each with the reason.
   *
   * Surfaced rather than swallowed. A magnet has no body and a drop-target bank is a rule
   * rather than an object, so an empty-handed builder is usually correct — but a device kind
   * nobody has written geometry for looks exactly the same from the outside, and the whole
   * point of a list is that the two are told apart by reading it rather than by noticing that
   * the playfield seems a bit bare.
   */
  readonly undrawn: string[] = []

  constructor(
    machine: () => LoadedMachine,
    dmdRaster: () => DmdRaster | undefined = () => undefined,
    lampState: () => LampStateReader | undefined = () => undefined,
    toyState: () => ToyStateReader | undefined = () => undefined,
    motionScale: () => number = () => 1,
    flashReduced: () => boolean = () => false,
    private readonly lighting: () => ComponentLightingState | undefined = () => undefined,
  ) {
    this.machine = machine
    this.dmdRaster = dmdRaster
    this.lampState = lampState
    this.toyState = toyState
    this.motionScale = motionScale
    this.flashReduced = flashReduced
    const loaded = machine()
    this.world = loaded.world
    this.definition = loaded.definition
    this.palette = new PartPalette(lightingProfileFor(this.definition.machineId).rubber)
  }

  build(scene: Scene, tier: RenderTier): void {
    // Here and not in `buildFurniture`, which runs *after* the surface and its lamps. Clearing
    // it there quietly erased everything they had already reported: a browser that refused the
    // insert map said so into a list that was emptied four lines later, so the one machine that
    // most needed the diagnostic was the one that never printed it.
    this.undrawn.length = 0
    // One group in the scene, and the machine inside it. `build` is called again after a context
    // loss, so this is `add` on an object that may already be there — Three treats re-adding a
    // child as a move and does not duplicate it.
    scene.add(this.root)
    const loaded = this.machine()
    this.world = loaded.world
    this.definition = loaded.definition
    const dims = this.definition.dimensions
    const incline = (dims.inclineDegrees * Math.PI) / 180

    const art = this.definition.art
    const seed = art?.seed ?? 0
    const cabinetArt = asRecord(art?.cabinet)
    const leftTexture = this.textureFor(cabinetArt?.['left'], seed, false, 'cabinet')
    const rightTexture = this.textureFor(cabinetArt?.['right'], seed, false, 'cabinet', true)
    const frontTexture = this.textureFor(asRecord(cabinetArt?.['frontApron']) ?? cabinetArt?.['front'], seed, false, 'cabinet', false, true)
    const backglassTexture = this.textureFor(art?.backglass, seed, false, 'backglass')

    // A machine may omit its cabinet block — a Builder machine in progress usually has. Falling
    // back to a standard Williams-era body is better than refusing to draw one: the scale a
    // player judges the ball against comes from the cabinet, and no cabinet at all reads as a
    // playfield floating in space rather than as a machine with a detail missing.
    const cabinet = buildCabinet(
      cabinetDimensionsFor(this.definition),
      {
        ...(leftTexture ? { left: leftTexture } : {}),
        ...(rightTexture ? { right: rightTexture } : {}),
        ...(frontTexture ? { front: frontTexture } : {}),
        ...(backglassTexture ? { backglass: backglassTexture } : {}),
          illustrated: packagedIllustration(this.definition.machineId, this.definition.machineVersion) !== undefined,
          machineId: this.definition.machineId,
      },
    )
    this.geometries.push(...cabinet.geometries)
    this.materials.push(...cabinet.materials)
    this.cabinetFrame.add(cabinet.group)
    this.root.add(this.cabinetFrame)
    registerCabinetFloor(this.root, cabinet.group, cabinetBottomY(cabinetDimensionsFor(this.definition)))
    this.buildDisplay(cabinet.backglass)
    this.lightingBackglass = cabinet.backglass
    const instructions = buildInstructionCards(this.definition)
    this.cabinetFrame.add(instructions.group)
    this.geometries.push(...instructions.geometries)
    this.materials.push(...instructions.materials)
    this.textures.push(...instructions.textures)

    // Playfield-local → world. Rotating by `incline − π/2` sends local +Y (up the playfield)
    // to mostly world −Z with a little +Y, and local +Z (the surface normal) to mostly world
    // +Y leaning towards the player: a playfield tilted 6.5° away, which is what it is.
    this.playfield.rotation.x = incline - Math.PI / 2
    this.playfield.position.set(0, cabinet.playfieldCentre.y, cabinet.playfieldCentre.z)
    this.root.add(this.playfield)
    this.playfield.add(this.frame)
    this.inclineSin = Math.sin(incline)
    this.inclineCos = Math.cos(incline)

    const hasFittedGlass = cabinet.group.children.some(part => part.name === 'glazing.authored.GlazingGlass')
    this.buildSurface(seed, hasFittedGlass)
    this.buildFurniture(tier)
    this.rebuildComponentLights()
    this.buildBalls(tier)
    this.buildLights(scene, tier)

    // The pool the machine throws on the floor it is standing on (M9-51, M5-19). Sized to the
    // cabinet's own footprint and hung off `root`, so a machine carried across a room takes its
    // light with it. Hidden until `matchRoom` is told there is a real floor to throw it on.
    const body = cabinetDimensionsFor(this.definition)
    const spill = buildLightSpill(body.width, body.length)
    this.spill = spill
    this.geometries.push(...spill.geometries)
    this.materials.push(...spill.materials)
    this.textures.push(...spill.textures)
    this.root.add(spill.mesh)

    // The body's paint, and the colour it was painted. See `painted`.
    this.painted = cabinet.tintable.map((material) => ({ material, base: material.color.clone() }))

    // M3-09. Last, so it is added after everything it covers — and worth drawing at all only
    // because `SceneHost` now gives the scene an environment to reflect. Glass with nothing to
    // reflect is a grey veil that dims the artwork and adds nothing.
    // The fitted cabinet already owns its pane. Retain the legacy pane for originals
    // and for cabinets whose prepared art or dimensions do not support the fitted kit.
    if (!hasFittedGlass) {
      const glass = buildGlass(dims.playfield.width, dims.playfield.length, dims.glassHeight)
      this.geometries.push(...glass.geometries)
      this.materials.push(...glass.materials)
      this.frame.add(glass.mesh)
    }
  }

  /**
   * Make the machine belong to the room it is standing in (M9-51, M5-19, 06 §3).
   *
   * **`lightingMatch.ts` has computed this since M5-19 and nothing read it.** Both clients derive
   * a `LightingMatch` from the runtime's light estimate every frame, both have a suite of tests
   * about the arithmetic, and `grep` for a reader of `XRFrameReport.lighting` returned the field's
   * own declaration and the line that assigns it. A cabinet lit for a neon arcade, standing in a
   * warm living room at night, read as a sticker — and the module's own header says so.
   *
   * Three effects, and each goes somewhere different:
   *
   *   * **the tint**, onto the body's paint and nothing else — see `CabinetBuild.tintable`, and
   *     note that the backglass is deliberately excluded because it is emissive;
   *   * **the ambient scale**, onto the machine's own hemisphere and key lights, so a dark room
   *     does not have a daylit cabinet;
   *   * **the spill**, onto the pool the cabinet throws on the floor — which is the effect the
   *     module calls first and the only one that needed something new drawn.
   *
   * **`undefined` is a flat screen or a VR arcade**, not a failure: the machine goes back to the
   * colours and the levels it was built with, and the pool is hidden. The pool is the one part
   * that is *not* neutral-by-default, because a pool of light on the arcade's own carpet is a
   * second opinion about a room this product already lights.
   */
  matchRoom(match: LightingMatch | undefined): void {
    const applied = match ?? NEUTRAL_MATCH
    for (const { light, base } of this.roomLit) light.intensity = base * applied.ambientScale
    for (const { material, base } of this.painted) {
      material.color.setRGB(
        base.r * applied.tintR,
        base.g * applied.tintG,
        base.b * applied.tintB,
      )
    }
    const spill = this.spill
    if (spill !== undefined) {
      spill.mesh.visible = match !== undefined
      spill.material.opacity = match === undefined ? 0 : applied.spill
    }
  }

  /**
   * A room pose in the playfield's own frame (M9-48).
   *
   * **The audio layer's `Spot` is PH-001**, the frame this file's header describes and this group
   * holds — +X right, +Y up the playfield, +Z out of the surface — so the one thing the mixer
   * needs to spatialise anything is the player's head expressed in it. The transform is asked of
   * `playfield` itself rather than rebuilt from the placement and the incline, because a second
   * copy of "where the playfield is" is how a renderer and a listener quietly stop agreeing: the
   * emitters are the positions this group draws at, and so, now, is the ear.
   *
   * **The scale divides out with everything else**, which is `worldToLocal`'s doing and is right:
   * a tabletop machine's emitters are half a metre apart to the eye and a full playfield apart to
   * this, so the listener has to be a full playfield away too or the picture and the sound would
   * describe different-sized objects. What it does mean is that a tabletop machine's *distances*
   * are full-size distances — a player leaning over a 0.42 machine is about 2.4 dB further from
   * it than one standing at a full-size cabinet — which is a level difference, not a direction
   * one, and is under the mixer's own bus trims.
   *
   * Allocates nothing: two scratch vectors, reused, because this runs once a frame inside the
   * headset's own callback.
   */
  listenerAt(
    x: number,
    y: number,
    z: number,
    fx: number,
    fy: number,
    fz: number,
  ): { x: number; y: number; z: number; fx: number; fy: number; fz: number } {
    // The placement was written this frame, by `XRRuntime`, and the render that refreshes the
    // world matrices has not happened yet — so ask for it rather than reading a stale one.
    this.playfield.updateWorldMatrix(true, false)
    const here = this.playfield.worldToLocal(EAR.set(x, y, z))
    // A direction, as the difference of two points: the rotation is what matters and a uniform
    // scale cancels in the subtraction that follows, so this needs no separate normal matrix.
    const ahead = this.playfield.worldToLocal(GAZE.set(x + fx, y + fy, z + fz))
    const dx = ahead.x - here.x
    const dy = ahead.y - here.y
    const dz = ahead.z - here.z
    const length = Math.sqrt(dx * dx + dy * dy + dz * dz)
    // A zero-length forward is a caller with no direction rather than a player facing nowhere.
    // Up the playfield is what a player at a machine is looking at, so that is the fallback.
    const inv = length > 1e-6 ? 1 / length : 0
    return {
      x: here.x,
      y: here.y,
      z: here.z,
      fx: dx * inv,
      fy: inv === 0 ? 1 : dy * inv,
      fz: dz * inv,
    }
  }

  /**
   * M3-17. The dot-matrix display, fitted to the window its own backglass art paints.
   *
   * Read from the art rather than positioned by hand, so a machine that moves its DMD moves
   * the panel with it and the two cannot get out of step. A machine with a display and no
   * painted window is reported rather than guessed at: putting a panel in the middle of
   * somebody's artwork is worse than not putting one anywhere.
   */
  private buildDisplay(backglass: { mesh: Mesh; width: number; height: number } | undefined): void {
    const display = this.definition.display
    const raster = this.dmdRaster()
    if (display === undefined || raster === undefined) return
    if (backglass === undefined) {
      this.undrawn.push(`${display.kind} display: this machine has no backglass to fit it to`)
      return
    }
    const surface = asRecord(this.definition.art?.backglass)
    const window = findDmdWindow(surface?.['program'])
    if (window === undefined) {
      this.undrawn.push(`${display.kind} display: the backglass art paints no dmdWindow`)
      return
    }

    this.dmd = buildDmdPanel(window, backglass.width, backglass.height, raster, display.palette)
    this.geometries.push(...this.dmd.geometries)
    this.materials.push(...this.dmd.materials)
    backglass.mesh.add(this.dmd.mesh)
    if(packagedIllustration(this.definition.machineId,this.definition.machineVersion)!==undefined){
      const placement=placePanel(window,backglass.width,backglass.height)
      addAuthoredDisplayFrame(this.definition.machineId,placement.width,placement.height,this.dmd.mesh,this.palette,this.geometries)
    }
  }

  /** M3-04. The wood, at true scale, wearing the machine's own art program. */
  private buildSurface(seed: number, hasFittedGlass: boolean): void {
    const { width, length } = this.definition.dimensions.playfield
    const geometry = scoopDeck(width, length, this.definition.devices, true)
    this.geometries.push(geometry)

    // `yUp` — the art program addresses the playfield the way the machine definition does,
    // with `y` running away from the player. See `artTexture.ts`.
    const map = this.textureFor(this.definition.art?.playfield, seed, true)
    const printed = packagedIllustration(this.definition.machineId, this.definition.machineVersion) !== undefined
    const printedFinish = hasFittedGlass || printed || this.definition.art?.playfield !== undefined
    const MaterialType = printedFinish ? MeshPhysicalMaterial : MeshStandardMaterial
    const material = new MaterialType({
      ...FINISHES.playfieldArt,
      ...(printedFinish ? reflectionFinish.board : {}),
      // Both procedural and illustrated paint are ink under glass. A second glossy board lobe
      // covers the artwork with a broad reflection even when the glass itself is hidden.
      ...(printedFinish ? printedBoardFinish : {}),
      ...(map ? { map } : { color: UNPAINTED_PLAYFIELD }),
    })
    this.materials.push(material)
    this.attachLamps(material)
    if(hasFittedGlass)attachContactCoating(material,this.definition)

    const mesh = new Mesh(geometry, material)
    // A `PlaneGeometry` is already in its group's XY plane with +Z out of it, which is exactly
    // the playfield frame — so there is nothing to rotate, and that is the point of putting the
    // group in machine coordinates in the first place.
    this.surfaceMesh=mesh;this.surfaceKey=scoopDeckKey(this.definition.devices)
    mesh.position.z = -0.0005
    this.frame.add(mesh)
    for (const layer of this.definition.layers) {
      if (layer.kind !== 'plane' || layer.minEntryZ === undefined || layer.bounds?.shape !== 'rect') continue
      const {min,max} = layer.bounds
      const plate = layer.id==='navDeck'?navAcrylicSlab(max[0]-min[0],max[1]-min[1],.008):new BoxGeometry(max[0]-min[0], max[1]-min[1], .008)
      this.geometries.push(plate)
      const deck = new Mesh(plate, this.palette.get(layer.id==='navDeck'?'navDeckSheet':'rampClearSheet'))
      deck.name = layer.id + '.supportedDeck'
      deck.position.set((min[0]+max[0])/2,(min[1]+max[1])/2,layer.z-.004)
      this.frame.add(deck)
      const a=raisedDeckInserts(this.definition,layer.id,layer.z),top=new BufferGeometry()
      top.setAttribute('position',new Float32BufferAttribute(a.positions,3));top.setAttribute('normal',new Float32BufferAttribute(a.normals,3));top.setAttribute('uv',new Float32BufferAttribute(a.uvs,2));top.setIndex(a.indices)
      this.geometries.push(top);if(a.indices.length>0){const face=new Mesh(top,material);face.name=layer.id+'.litSurface';this.frame.add(face)}
      for(const collider of this.definition.colliders){if(collider.kind!=='circle'||!collider.id.startsWith('navSupport'))continue
        const shaft=new BoxGeometry(.006,.006,layer.z-.008);this.geometries.push(shaft);const leg=new Mesh(shaft,this.palette.get('coilArm'));leg.position.set(...collider.centre,(layer.z-.008)/2);this.frame.add(leg)
      }
    }
  }

  /**
   * M3-15. Sixty inserts, as emissive texels on the playfield's own material.
   *
   * `onBeforeCompile` rather than a custom material: the playfield is a lit, textured, tone-
   * mapped PBR surface and reimplementing all of that to add one texture fetch would be
   * trading a two-line hook for a shader nobody can maintain. The inserts land in
   * `totalEmissiveRadiance`, the same channel an emissive map feeds, so they go through tone
   * mapping like every other light in the scene rather than being pasted on afterwards at
   * whatever brightness looked right on one monitor.
   *
   * The uniforms are held on the material's `userData` because `onBeforeCompile` is called
   * once, lazily, at the first render — anything the update path needs has to survive from
   * here to there, and a closure over a local would be recreated on every rebuild.
   */
  private attachLamps(material: MeshStandardMaterial): void {
    this.lampMaterial = material
    let lamps: BuiltLamps
    try {
      lamps = buildLamps(this.definition,undefined,packagedIllustration(this.definition.machineId,this.definition.machineVersion)!==undefined)
    } catch (error) {
      // A browser that will not give a 2-D context cannot have an insert map, and a playfield
      // with no lamps is far better than no playfield.
      this.undrawn.push(`lamps: ${error instanceof Error ? error.message : String(error)}`)
      return
    }
    this.lamps = lamps
    material.normalMap = lamps.optics?.atlas ?? null
    this.undrawn.push(...lamps.skipped)
    if (lamps.lampCount === 0) return

    const gi = buildGi(this.definition)
    this.gi = gi
    this.undrawn.push(...gi.skipped)

    const uniforms = {
      uLampMap: { value: lamps.map },
      uLampState: { value: lamps.state },
      uLampCount: { value: lamps.lampCount },
      uGiRect: { value: gi.rects },
      uGiColour: { value: gi.colours },
      uGiCount: { value: gi.count },
      // Far under the inserts' gain, and lower than it first looked right on paper. General
      // illumination is the light you stop noticing after ten seconds; at 0.22 it washed
      // NEON NOVA's playfield pale and flattened the artwork it is supposed to be lighting,
      // and the inserts stopped standing out from it at all — which is the one job the
      // contrast between them has to do.
      uGiGain: { value: 0.07 },
      // A twelfth of the playfield's width of softness each side of the boundary. Enough that
      // no player can point at where a zone ends, which is the entire difference between
      // general illumination and a rectangle of light.
      uGiFeather: { value: 0.08 },
      // Under 1, and it took seeing it to believe it. An insert's colour is added to
      // `totalEmissiveRadiance` and then tone-mapped, and ACES desaturates as it approaches
      // white — so a gain that makes the inserts satisfyingly *bright* makes them all the same
      // colour, and a machine whose cyan lanes, amber targets and magenta arrows are 38
      // identical white blobs has lost the only thing inserts are for. Below 1 they keep their
      // hue and still read as lit, because the paint around them is not emissive at all.
      uLampGain: { value: 0.85 },
    }
    material.userData['lampUniforms'] = uniforms
    const optical:InsertOpticsUniforms={
      uOpticsAtlas:{value:lamps.optics?.atlas??lamps.map},uOpticsParams:{value:lamps.optics?.parameters??lamps.state},
      uOpticsSelector:{value:lamps.optics?.selector??lamps.map},uOpticsSelectorGroups:{value:lamps.optics?.selectorGroups??1},
      uOpticsBoardSize:{value:new Vector2(this.definition.dimensions.playfield.width,this.definition.dimensions.playfield.length)},
      uOpticsTileCount:{value:lamps.optics?.tileCount??1},uOpticsTileSize:{value:lamps.optics?.tileSize??1},
    }
    material.userData['insertOpticsUniforms']=optical
    material.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, uniforms,optical)
      shader.vertexShader='varying float vInsertSurfaceZ;\n'+shader.vertexShader.replace('#include <begin_vertex>','#include <begin_vertex>\nvInsertSurfaceZ=position.z;')
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <roughnessmap_fragment>',INSERT_OPTICS_ROUGHNESS)
        .replace('#include <normal_fragment_maps>',INSERT_OPTICS_NORMAL)
        .replace(
          'void main() {',
          `${LAMP_UNIFORM_DECLARATIONS}\n${GI_UNIFORM_DECLARATIONS}\n${INSERT_OPTICS_DECLARATIONS}\nvoid main() {`,
        )
        .replace(
          '#include <emissivemap_fragment>',
          `#include <emissivemap_fragment>\n\n#ifdef USE_NORMALMAP_TANGENTSPACE\n${INSERT_OPTICS_LIGHT}\n#else\n${LAMP_FRAGMENT_HOOK}\n#endif\n\n${GI_FRAGMENT_HOOK}`,
        )
    }
    material.customProgramCacheKey = () => 'HCC-playfield-optics-gi-reflectance-01'
    // Changing `onBeforeCompile` after a material has been used needs the program rebuilt.
    material.needsUpdate = true
  }

  /**
   * M3-05 and M3-06. Guides, posts, mechanisms, ramps and habitrails, in playfield
   * coordinates.
   *
   * Three builders, split on the only line that matters: `rails` draws what never moves,
   * `deviceMeshes` draws what does — handing back a closure per mechanism that poses it from
   * the simulation — and `ramps` draws the layers above the wood. Nothing in any of them
   * decides anything about the game.
   */
  private buildFurniture(tier: RenderTier): void {
    const illustrated = packagedIllustration(this.definition.machineId, this.definition.machineVersion) !== undefined
    const rails = buildRails(this.definition, this.palette, illustrated,
      this.materials.find(m => m.name === 'cabinet.brushedSteel'))
    this.railsDrawn = rails
    this.undrawn.push(...rails.skipped)
    this.frame.add(rails.group)

    // Recorded one device at a time (M13-04), so `patch` can replace one later without
    // touching the rest. The device geometries live in `drawn` rather than in `geometries`,
    // which is what lets one device be disposed on its own.
    if (illustrated) {
      const texture = this.textureFor(this.definition.art?.plastics, this.definition.art?.seed ?? 0)
      const material = this.palette.get('printedPlastic') as MeshStandardMaterial
      if (texture !== undefined) { material.map = texture; material.needsUpdate = true }
    }
    const devices = buildDevices(this.machine(), this.palette, illustrated,
      this.materials.find(m => m.name === 'cabinet.brushedSteel'))
    this.devicesGroup = devices.group
    for (const { device, built } of devices.each) this.remember(device, built)
    this.refreshAnimators()
    this.frame.add(devices.group)

    const ramps = buildRamps(this.machine(), this.palette, packagedIllustration(this.definition.machineId, this.definition.machineVersion) !== undefined,
      this.materials.find(m=>m.name==='cabinet.brushedSteel'))
    this.geometries.push(...ramps.geometries)
    this.undrawn.push(...ramps.skipped)
    this.frame.add(ramps.group)

    // M3-16. The tier decides whether the machine can afford the shared flasher light; the
    // domes are drawn either way, because the dome is the thing a player looks at and the
    // wash is the thing they feel.
    this.flasherWithLight=TIER_BUDGETS[tier].dynamicLights>=2
    const flashers = buildFlashers(this.definition, this.flasherWithLight)
    this.flashers = flashers
    this.geometries.push(...flashers.geometries)
    this.materials.push(...flashers.materials)
    this.textures.push(...flashers.textures)
    this.undrawn.push(...flashers.skipped)
    this.frame.add(flashers.group)

    // M3-07. After the flashers and before the glass, which is the order they sit in on the
    // wood: the dome stands proud of the playfield and the glass covers everything.
    const toys = buildToys(this.definition, this.motionScale())
    this.toys = toys
    this.geometries.push(...toys.geometries)
    this.materials.push(...toys.materials)
    this.undrawn.push(...toys.skipped)
    this.frame.add(toys.group)
  }

  /**
   * M3-08. Chrome, and a dark pole so its spin is visible.
   *
   * The arithmetic lives in `balls.ts` so it can be tested without a screen — including the
   * one thing about it that is easy to get wrong and impossible to see, which is that
   * `Ball.z` is already the centre height and nothing may be added to it.
   */
  private buildBalls(tier: RenderTier): void {
    const materials=createBallMaterials(),material=materials[0]!,poleMaterial=materials[1]??material
    this.materials.push(...materials)
    this.ballMaterials = materials
    this.ballSegments = tier === 'low' ? 16 : 32

    this.balls = buildBalls(this.world, material, poleMaterial, this.ballSegments,this.definition.devices)
    this.geometries.push(...this.balls.geometries)
    // On `playfield` and **not** on `frame`: a ball's coordinates are absolute, and the whole
    // point of the frame group is that the machine moves around the ball rather than with it.
    this.playfield.add(this.balls.group)
  }

  /**
   * The balls, built again for the world the provider answers now (M13-10).
   *
   * `buildBalls` closes over `world.balls`, so a rebuilt session leaves the drawn balls posing
   * from a world nothing steps — a ball served in the new world would be invisible, and the old
   * world's would sit in the trough for ever. Same materials, same segment count: only the
   * closure changes, and the geometry it needs is a handful of spheres.
   */
  private rebuildBalls(): void {
    const old = this.balls
    const materials = this.ballMaterials
    if (old === undefined || materials === undefined) return
    this.playfield.remove(old.group)
    for (const g of old.geometries) {
      g.dispose()
      const at = this.geometries.indexOf(g)
      if (at >= 0) this.geometries.splice(at, 1)
    }
    this.balls = buildBalls(this.world, materials[0]!, materials[1]??materials[0]!, this.ballSegments,this.definition.devices)
    this.geometries.push(...this.balls.geometries)
    this.playfield.add(this.balls.group)
  }

  /**
   * Lighting, within the tier's budget (ARCH-010).
   *
   * Inserts are deliberately absent from this list. Sixty independently flashing inserts are
   * sixty lights only if you build them that way; they are emissive texels driven by one lamp
   * texture (ARCH-011, M3-15), which costs one upload and no draw calls.
   */
  private buildLights(_scene: Scene, tier: RenderTier): void {
    const budget = TIER_BUDGETS[tier].dynamicLights

    const ambient = new HemisphereLight(0xbac4c7, 0x111412, 0.55)
    this.root.add(ambient)

    const key = new DirectionalLight(0xffffff, 1.5)
    key.position.set(0.6, 2.2, 1.1)
    this.root.add(key)

    // **Kept, with the intensity they were built at** (M9-51). `matchRoom` scales *from* these
    // rather than from whatever it last set: a multiplication applied to its own output is a room
    // that gets darker every frame, and one frame of a dim estimate would put the machine out.
    this.roomLit = [
      { light: ambient, base: ambient.intensity },
      { light: key, base: key.intensity },
    ]

    if (budget >= 3) {
      const fill = new PointLight(0xe9eff2, 6, 2.4, 2)
      fill.position.set(-0.35, 1.35, -0.35)
      this.root.add(fill)
    }
  }

  /**
   * Pose everything for one frame.
   *
   * `prev → current` at `alpha`, and nothing else. The solver produced both endpoints; this
   * only chooses a point between them.
   */
  update(alpha: number): void {
    this.balls?.animate(alpha)

    // The nudge (PH-070). Read straight from the solver's own frame displacement, so the
    // machine on screen is exactly as far out of place as the machine the ball is bouncing
    // off — up to 12 mm, which is small, and unmistakable the moment you shove it.
    const { frameX, frameY } = this.world
    this.frame.position.set(frameX, frameY, 0)
    this.cabinetFrame.position.set(frameX, frameY * this.inclineSin, -frameY * this.inclineCos)
    this.palette.guideBalls(this.balls?.group)

    // The lamps, from the rule engine. `stepIndex` and not a clock: a lamp blinking off the
    // wall clock drifts away from the game driving it whenever the renderer stutters, and the
    // tell is that a shot's lamp goes dark exactly as you make the shot.
    // The toy, before the illumination it can put out. 05 §3's BIG BANG is "a full white flash,
    // corona blown outward, playfield general illumination cut to black for 400 ms" — one event
    // with two halves, and the half that reads as *enormous* is the room going dark.
    const toyRead = this.toyState()
    if (toyRead !== undefined) this.toys?.update(toyRead, this.world.stepIndex, this.motionScale())

    const read = this.lampState()
    if (read !== undefined) {
      const step = this.world.stepIndex
      const reduced = this.flashReduced()
      this.lamps?.update(read, step, reduced, this.lighting())
      this.gi?.update(read, step, this.toys?.dim() ?? 0, reduced)
      const lighting = this.lighting()
      if (lighting !== undefined && this.gi !== undefined) {
        const [r, g, b] = lighting.modeColour(), tint = lighting.modeTint
        const show = lighting.showLevel(reduced)
        for (const colour of this.gi.colours) {
          colour.x = colour.x * (1 - tint) + r * tint * 0.35 + r * show * 0.08
          colour.y = colour.y * (1 - tint) + g * tint * 0.35 + g * show * 0.08
          colour.z = colour.z * (1 - tint) + b * tint * 0.35 + b * show * 0.08
        }
      }
      this.flashers?.update(read, step, reduced)
    }
    this.componentLights?.update(this.lighting(), read, this.world.stepIndex, this.flashReduced(), this.flashers?.light)

    // The display, from whatever the game's driver last drew. Uploaded rather than diffed:
    // the whole raster is 4 kB, so checking whether it changed costs about as much as
    // sending it and adds a way to be wrong.
    if (this.dmd !== undefined) {
      const raster = this.dmdRaster()
      if (raster !== undefined) this.dmd.update(raster)
    }

    // Mechanisms. `stepIndex` rather than a clock: a bumper's ring is down because the coil
    // fired eleven *steps* ago, so the machine animates identically at every frame rate.
    const step = this.world.stepIndex
    for (const animate of this.animators) animate(alpha, step)
  }

  dispose(): void {
    this.clearDrops()
    this.componentLights?.dispose()
    this.componentLights = undefined
    this.lightingBackglass = undefined
    for (const g of this.railsDrawn?.geometries ?? []) g.dispose()
    this.railsDrawn = undefined
    for (const g of this.geometries) g.dispose()
    for (const m of this.materials) m.dispose()
    for (const t of this.textures) t.dispose()
    this.palette.dispose()
    this.palette = new PartPalette(lightingProfileFor(this.definition.machineId).rubber)
    this.geometries.length = 0
    this.materials.length = 0
    this.textures.length = 0
    // Disposed with everything else above — these are handles into the three lists, not a second
    // set of objects — but the *references* have to go, or a rebuilt machine tints the paint of
    // the machine it replaced and scales lights that are no longer in any scene.
    this.roomLit = []
    this.painted = []
    this.spill = undefined
    this.balls = undefined;this.surfaceMesh=undefined;this.surfaceKey=''
    this.dmd?.dispose()
    this.dmd = undefined
    this.toys = undefined
    this.lamps?.dispose()
    this.lamps = undefined
    this.gi = undefined
    this.flashers = undefined
    this.animators = []
    for (const entry of this.drawn.values()) for (const g of entry.geometries) g.dispose()
    this.drawn.clear()
    this.devicesGroup = undefined
    this.ghost(undefined)
    this.highlight(undefined)
    if (this.guidesDrawn !== undefined) {
      this.playfield.remove(this.guidesDrawn.group)
      this.guidesDrawn.dispose()
      this.guidesDrawn = undefined
    }
    if (this.validationDrawn !== undefined) {
      this.playfield.remove(this.validationDrawn.group)
      this.validationDrawn.dispose()
      this.validationDrawn = undefined
    }
    if (this.inspectorDrawn !== undefined) {
      this.playfield.remove(this.inspectorDrawn.group)
      this.inspectorDrawn.dispose()
      this.inspectorDrawn = undefined
    }
    if (this.miniMapDrawn !== undefined) {
      this.playfield.remove(this.miniMapDrawn.group)
      this.miniMapDrawn.dispose()
      this.miniMapDrawn = undefined
    }
    if (this.rampHandlesDrawn !== undefined) {
      this.playfield.remove(this.rampHandlesDrawn.group)
      this.rampHandlesDrawn.dispose()
      this.rampHandlesDrawn = undefined
    }
    if (this.ribbonDrawn !== undefined) {
      this.playfield.remove(this.ribbonDrawn.group)
      this.ribbonDrawn.dispose()
      this.ribbonDrawn = undefined
    }
    this.ballMaterials = undefined
    this.undrawn.length = 0
    this.frame.clear()
    this.playfield.clear()
    this.cabinetFrame.clear()
    this.root.clear()
  }

  /**
   * Bring the drawn devices up to date with the machine's definition, one device at a time
   * (M13-04).
   *
   * Reads the provider again, so a caller that has loaded a new `LoadedMachine` from an edited
   * definition needs only to make the provider answer it and call this. A device whose id is
   * gone is removed and its geometry disposed; a new id is drawn; an id whose definition object
   * is *not the same object* is redrawn — identity, not deep equality, because the Builder's
   * draft never mutates a device it did not touch, and comparing sixteen kinds field by field is
   * a second copy of the format. Everything else — the surface art, the lamps, the cabinet, the
   * glass, the balls — is untouched: a layout edit costs the devices it changed and nothing
   * else, which is what lets a machine be edited under a headset's frame budget.
   *
   * **A new world rebinds everything and rebuilds nothing** (M13-10). Build mode rebuilds the
   * session on every commit, so the provider answers a `LoadedMachine` whose `world` is a new
   * object even when only one device moved. Identity of the *definition* decides whether a
   * device's geometry is rebuilt; identity of the *world* decides whether its animator is pointed
   * at a new runtime — every untouched device is rebound through `BuiltDevice.rebind`, and the
   * balls, which close over `world.balls`, are built again. Without this the picture would be
   * the defect this repository names in so many words: the same bytes loaded twice, and the
   * renderer drawing the graph nobody simulates.
   *
   * Answers what changed, for whoever is counting.
   */
  patch(): PatchReport {
    const loaded = this.machine()
    const rebound = loaded.world !== this.world
    const was = this.definition
    this.world = loaded.world
    this.definition = loaded.definition
    if(this.lampMaterial!==undefined&&(this.definition.art?.playfield!==was.art?.playfield||this.definition.art?.seed!==was.art?.seed)){
      const material=this.lampMaterial,previous=material.map
      material.map=this.textureFor(this.definition.art?.playfield,this.definition.art?.seed??0,true)??null
      material.needsUpdate=true
      if(previous!==null){const slot=this.textures.indexOf(previous);if(slot>=0)this.textures.splice(slot,1);previous.dispose()}
    }
    refreshContactCoating(this.lampMaterial,this.definition)
    // A device carries its body and its insert (`carryAttachments`, the 1.0.104 round): a changed
    // collider list draws the rails again, a changed lamp list paints the insert map again.
    if (loaded.definition.colliders !== was.colliders || postPlacementKey(loaded.definition) !== postPlacementKey(was)) this.redrawRails()
    if (loaded.definition.lamps !== was.lamps) this.repaintInserts()
    if(loaded.definition.lamps!==was.lamps||loaded.definition.colliders!==was.colliders)this.redrawFlashers()
    const added: string[] = []
    const removed: string[] = []
    const changed: string[] = []
    if (this.devicesGroup === undefined) return { added, removed, changed, rebound: false }

    const next = new Map(loaded.definition.devices.map((d) => [d.id, d] as const))
    for (const [id, entry] of [...this.drawn]) {
      const definition = next.get(id)
      if (definition === undefined) {
        this.discard(id, entry)
        removed.push(id)
      } else if (definition !== entry.definition || (definition.kind==='plunger'&&isSecondaryManual(definition,was.devices)!==isSecondaryManual(definition,this.definition.devices)) || (definition.kind === 'slingshot'
        && lowerAssemblyMatches(was, id) !== lowerAssemblyMatches(this.definition, id))) {
        this.discard(id, entry)
        this.draw(definition, loaded)
        changed.push(id)
      } else if (rebound) {
        entry.rebind(runtimeFor(loaded, definition))
      }
    }
    for (const definition of loaded.definition.devices) {
      if (this.drawn.has(definition.id)) continue
      this.draw(definition, loaded)
      added.push(definition.id)
    }
    const deckKey=scoopDeckKey(this.definition.devices)
    if(this.surfaceMesh!==undefined&&deckKey!==this.surfaceKey){
      const {width,length}=this.definition.dimensions.playfield,previous=this.surfaceMesh.geometry
      const next=scoopDeck(width,length,this.definition.devices,true)
      const slot=this.geometries.indexOf(previous);if(slot>=0)this.geometries[slot]=next;else this.geometries.push(next)
      this.surfaceMesh.geometry=next;this.surfaceKey=deckKey;previous.dispose()
    }
    if (rebound) this.rebuildBalls()
    this.refreshAnimators()
    if (rebound || added.length + removed.length + changed.length > 0 || was.colliders !== this.definition.colliders || was.lamps !== this.definition.lamps || was.layers !== this.definition.layers) this.rebuildComponentLights()
    return { added, removed, changed, rebound }
  }

  private rebuildComponentLights(): void {
    const group = this.componentLights?.group
    this.componentLights?.dispose()
    this.componentLights = buildComponentLights(this.machine(), this.frame, this.lightingBackglass, group)
    ;(this.devicesGroup ?? this.frame).add(this.componentLights.group)
    this.componentLights.lift(this.lifted)
  }

  /** Draw the static rails again from the definition — a moved device carried a collider with it. */
  private redrawRails(): void {
    const old = this.railsDrawn
    if (old !== undefined) {
      old.group.parent?.remove(old.group)
      for (const g of old.geometries) g.dispose()
    }
    const rails = buildRails(this.definition, this.palette,
      packagedIllustration(this.definition.machineId, this.definition.machineVersion) !== undefined,
      this.materials.find(m => m.name === 'cabinet.brushedSteel'))
    this.railsDrawn = rails
    this.frame.add(rails.group)
  }

  private redrawFlashers():void{
    const old=this.flashers
    if(old){
      old.group.parent?.remove(old.group)
      for(const g of old.geometries){const at=this.geometries.indexOf(g);if(at>=0)this.geometries.splice(at,1);g.dispose()}
      for(const m of old.materials){const at=this.materials.indexOf(m);if(at>=0)this.materials.splice(at,1);m.dispose()}
      for(const t of old.textures){const at=this.textures.indexOf(t);if(at>=0)this.textures.splice(at,1);t.dispose()}
    }
    const made=buildFlashers(this.definition,this.flasherWithLight);this.flashers=made;this.frame.add(made.group)
    this.geometries.push(...made.geometries);this.materials.push(...made.materials);this.textures.push(...made.textures)
  }

  /** Paint the insert map again — a moved device carried its lamp — and hand it to the playfield. */
  private repaintInserts(): void {
    const material = this.lampMaterial
    const uniforms = material?.userData['lampUniforms'] as
      | { uLampMap: { value: unknown }; uLampState: { value: unknown }; uLampCount: { value: number } }
      | undefined
    if (material === undefined || uniforms === undefined) return
    let lamps: BuiltLamps
    try {
      lamps = buildLamps(this.definition,undefined,packagedIllustration(this.definition.machineId,this.definition.machineVersion)!==undefined)
    } catch {
      return
    }
    this.lamps?.dispose()
    this.lamps = lamps
    const hadOptics = material.normalMap !== null
    material.normalMap = lamps.optics?.atlas ?? null
    if (hadOptics !== (lamps.optics !== undefined)) material.needsUpdate = true
    const optical=material.userData['insertOpticsUniforms'] as InsertOpticsUniforms
    optical.uOpticsAtlas.value=lamps.optics?.atlas??lamps.map;optical.uOpticsParams.value=lamps.optics?.parameters??lamps.state
    optical.uOpticsSelector.value=lamps.optics?.selector??lamps.map;optical.uOpticsSelectorGroups.value=lamps.optics?.selectorGroups??1
    optical.uOpticsTileCount.value=lamps.optics?.tileCount??1;optical.uOpticsTileSize.value=lamps.optics?.tileSize??1
    optical.uOpticsBoardSize.value.set(this.definition.dimensions.playfield.width,this.definition.dimensions.playfield.length)
    uniforms.uLampMap.value = lamps.map
    uniforms.uLampState.value = lamps.state
    uniforms.uLampCount.value = lamps.lampCount
  }

  /**
   * Draw — or take down — the ghost of a part Build mode is carrying (M13-10).
   *
   * The very device `previewOf` would commit, built by `buildDeviceMesh` from the definition
   * alone (no runtime, so nothing animates it) with the palette's materials cloned see-through.
   * In the playfield's own frame, under `playfield` rather than `frame`, so it sits where the
   * part will land and not where a nudge has shoved the machine this frame. The same device
   * object twice is one ghost: the carry hands back one object per snapped point, so a hand
   * jittering inside a catch radius does not rebuild anything.
   */
  ghost(
    device: MachineDevice | import('../builder/parts').FlasherPart | undefined,
    shift?: readonly [number, number, number],
  ): void {
    if (device === undefined) this.lift(undefined)
    const drawn = this.ghostDrawn
    if (drawn !== undefined && drawn.device === device) {
      // The same part, moved: a placed part in hand off the board rides the hand without a rebuild.
      const base = drawn.base ?? [0, 0, 0]
      drawn.group.position.set(base[0] + (shift?.[0] ?? 0), base[1] + (shift?.[1] ?? 0), base[2] + (shift?.[2] ?? 0))
      paintGhostOnTop(drawn, shift !== undefined)
      return
    }
    if (drawn !== undefined) {
      this.playfield.remove(drawn.group)
      for (const g of drawn.geometries) g.dispose()
      for (const m of drawn.materials) m.dispose()
      this.ghostDrawn = undefined
    }
    if (device === undefined) return
    const built = withSlingAssembly(buildDeviceMesh(device, this.palette,undefined,false,false,this.definition),device,this.definition,this.palette)
    if (built.group === undefined) return
    const materials: Material[] = []
    built.group.traverse((node) => {
      const mesh = node as Mesh
      if (!mesh.isMesh) return
      // The `ghost` finish (M13-18), unlit: a hologram of the part rather than the part faded,
      // and the same one the native bench draws.
      const material = new MeshBasicMaterial({
        color: GHOST_COLOR,
        transparent: true,
        opacity: GHOST_OPACITY,
        depthWrite: false,
      })
      mesh.material = material
      materials.push(material)
    })
    built.group.name = 'build.ghost'
    const base: [number, number, number] = [built.group.position.x, built.group.position.y, built.group.position.z]
    built.group.position.set(base[0] + (shift?.[0] ?? 0), base[1] + (shift?.[1] ?? 0), base[2] + (shift?.[2] ?? 0))
    this.playfield.add(built.group)
    this.ghostDrawn = { device, group: built.group, base, geometries: built.geometries, materials }
    paintGhostOnTop(this.ghostDrawn, shift !== undefined)
  }

  /**
   * The part in hand let go over the remove tray falls into it (24 Oct 2026, the owner on the
   * native 1.0.211: "when user lets go of component it disappears instead of showing being dropped
   * into remove box"). The ghost as it is now — its own copies of the geometry and materials it can
   * fade — falls to `floorUnder` of its lowest point, rests `DROP_REST_SECONDS` and fades over
   * `DROP_FADE_SECONDS`; the ghost itself is taken down, as a release always does. False with no
   * ghost to drop. The native bench's `BuildPicture.DropGhost`.
   */
  dropGhost(floorUnder: (lowest: Vector3) => Vector3): boolean {
    const drawn = this.ghostDrawn
    if (drawn === undefined) return false
    let root: Object3D = this.playfield
    while (root.parent !== null) root = root.parent
    const body = drawn.group.clone(true)
    body.name = 'build.ghost.dropped'
    drawn.group.parent?.add(body)
    body.updateMatrixWorld(true)
    root.attach(body)
    const geometries: BufferGeometry[] = []
    const materials: MeshBasicMaterial[] = []
    let opacity = GHOST_OPACITY
    body.traverse((node) => {
      const mesh = node as Mesh
      if (!mesh.isMesh) return
      mesh.geometry = mesh.geometry.clone()
      geometries.push(mesh.geometry)
      const material = (mesh.material as MeshBasicMaterial).clone()
      opacity = material.opacity
      mesh.material = material
      materials.push(material)
    })
    const box = new Box3().setFromObject(body)
    if (box.isEmpty()) {
      root.remove(body)
      for (const g of geometries) g.dispose()
      for (const m of materials) m.dispose()
      return false
    }
    const lowest = new Vector3((box.min.x + box.max.x) / 2, box.min.y, (box.min.z + box.max.z) / 2)
    const from = body.position.clone()
    const to = from.clone().add(floorUnder(lowest).sub(lowest))
    this.drops.push({ body, geometries, materials, opacity, from, to, age: 0 })
    this.ghost(undefined)
    return true
  }

  /** Parts still falling, resting or fading in the remove tray. */
  get dropsShowing(): number {
    return this.drops.length
  }

  /** One frame of the parts dropped into the remove tray: falling, resting, fading, gone. */
  advanceDrops(seconds: number): void {
    for (let i = this.drops.length - 1; i >= 0; i -= 1) {
      const drop = this.drops[i] as Dropped
      drop.age += Math.max(0, seconds)
      const height = Math.abs(drop.from.y - drop.to.y)
      const falling = height > 1e-4 ? Math.sqrt((2 * height) / DROP_GRAVITY) : 0
      if (drop.age < falling) {
        drop.body.position.lerpVectors(drop.from, drop.to, drop.age / falling)
        drop.body.position.y = drop.from.y + Math.sign(drop.to.y - drop.from.y) * Math.min(height, 0.5 * DROP_GRAVITY * drop.age * drop.age)
        continue
      }
      drop.body.position.copy(drop.to)
      const fadeFrom = falling + DROP_REST_SECONDS
      if (drop.age < fadeFrom) continue
      const left = 1 - (drop.age - fadeFrom) / DROP_FADE_SECONDS
      if (left <= 0) {
        this.finishDrop(drop)
        this.drops.splice(i, 1)
        continue
      }
      for (const material of drop.materials) material.opacity = drop.opacity * left
    }
  }

  /** Take every dropped part away at once — leaving the mode, a test, a recycle. */
  clearDrops(): void {
    for (const drop of this.drops) this.finishDrop(drop)
    this.drops.length = 0
  }

  private finishDrop(drop: Dropped): void {
    drop.body.removeFromParent()
    for (const g of drop.geometries) g.dispose()
    for (const m of drop.materials) m.dispose()
  }

  /**
   * Hide the drawn copy of the placed part in hand, or show whichever was hidden again
   * (undefined). While a placed part is carried the ghost is the part: the copy left standing
   * where it was read on the headset as a duplicate (the 1.0.103 round, 26 Sep 2026).
   */
  lift(deviceId: string | undefined): void {
    this.componentLights?.lift(deviceId)
    for(const child of this.flashers?.group.children??[])child.visible=child.name!==deviceId&&child.name!==deviceId+'.flasher.spill'
    if (deviceId === this.lifted) return
    if (this.lifted !== undefined) {
      const was = this.drawn.get(this.lifted)?.group
      if (was !== undefined) was.visible = true
    }
    this.lifted = deviceId
    if (deviceId === undefined) return
    const now = this.drawn.get(deviceId)?.group
    if (now !== undefined) now.visible = false
  }

  /** The placed part whose drawn copy `lift` has hidden, or none. */
  get liftedDevice(): string | undefined {
    return this.lifted
  }

  /**
   * Draw what the snap looks like — the guides, the marks, the reticle, the label — or take it
   * all down (M13-13). `flash` is true on the frame a rule changed.
   */
  guides(view: SnapView | undefined, seconds: number, flash: boolean): void {
    if (view === undefined && this.guidesDrawn === undefined) return
    if (this.guidesDrawn === undefined) {
      this.guidesDrawn = buildSnapGuides()
      this.playfield.add(this.guidesDrawn.group)
    }
    this.guidesDrawn.update(view, seconds, flash)
  }

  /**
   * Draw the report on the machine — the decal, the callouts, the measurements — or take it all
   * down (M13-17). `scale` is the bench's, for room-sized callouts.
   */
  validation(view: ValidationView | undefined, scale: number): void {
    if (view === undefined && this.validationDrawn === undefined) return
    if (this.validationDrawn === undefined) {
      this.validationDrawn = buildValidationOverlay()
      this.playfield.add(this.validationDrawn.group)
    }
    this.validationDrawn.update(view, scale)
  }

  /**
   * Draw the inspector card beside the selected part — its rows, rails, steppers and values —
   * or take it down (M13-22). `scale` is the bench's, for a room-sized card; `held` is the row
   * a pinch is sliding, whose knob is lit.
   */
  inspector(view: InspectorView | undefined, scale: number, held?: string): void {
    if (view === undefined && this.inspectorDrawn === undefined) return
    if (this.inspectorDrawn === undefined) {
      this.inspectorDrawn = buildInspectorCard()
      this.playfield.add(this.inspectorDrawn.group)
    }
    this.inspectorDrawn.update(view, scale, held)
  }

  /**
   * Draw the mini-map beside the bench — the plan, the nudge pads — or take it down (M13-23).
   * `scale` is the bench's, for a room-sized map.
   */
  miniMap(view: MiniMapView | undefined, scale: number): void {
    if (view === undefined && this.miniMapDrawn === undefined) return
    if (this.miniMapDrawn === undefined) {
      this.miniMapDrawn = buildMiniMap()
      this.playfield.add(this.miniMapDrawn.group)
    }
    this.miniMapDrawn.update(view, scale)
  }

  /**
   * Draw the selected ramp's control points — the handles, the one in hand, the curve as it is
   * being shaped — or take them down (M13-24). `scale` is the bench's, for fingertip-sized handles.
   */
  /**
   * Draw the last three seconds of the ball's path through the machine, with a dot at every
   * contact — or take the ribbon down (M13-25). Rewritten every frame from the trail.
   */
  ribbon(trail: BallTrail | undefined, legend?: readonly RibbonLegendRow[]): void {
    if (trail === undefined && this.ribbonDrawn === undefined) return
    if (this.ribbonDrawn === undefined) {
      const dims = this.definition.dimensions
      this.ribbonDrawn = buildBallRibbon(undefined, legendAt(dims.playfield.width, dims.playfield.length, dims.glassHeight))
      this.playfield.add(this.ribbonDrawn.group)
    }
    this.ribbonDrawn.update(trail, legend)
  }

  rampPoints(view: PointsView | undefined, scale: number): void {
    if (view === undefined && this.rampHandlesDrawn === undefined) return
    if (this.rampHandlesDrawn === undefined) {
      this.rampHandlesDrawn = buildRampHandles()
      this.playfield.add(this.rampHandlesDrawn.group)
    }
    this.rampHandlesDrawn.update(view, scale)
  }

  /**
   * Light the placed part with this id as the one under the fingertip, or take the halo down.
   *
   * Cheap to call every frame: a halo already up for this id is left alone. The copy is built
   * from the definition the part was drawn with, so it is the part's own shape; it is not
   * animated, because a halo is a label and not a second flipper.
   */
  highlight(deviceId: string | undefined): void {
    const drawn = this.haloDrawn
    if (drawn !== undefined && drawn.id === deviceId) return
    if (drawn !== undefined) {
      this.playfield.remove(drawn.group)
      for (const g of drawn.geometries) g.dispose()
      for (const m of drawn.materials) m.dispose()
      this.haloDrawn = undefined
    }
    if (deviceId === undefined) return
    const device = this.drawn.get(deviceId)?.definition
    if (device === undefined) return
    const built = withSlingAssembly(buildDeviceMesh(device, this.palette,undefined,false,false,this.definition),device,this.definition,this.palette)
    if (built.group === undefined) return
    const materials: Material[] = []
    built.group.traverse((node) => {
      const mesh = node as Mesh
      if (!mesh.isMesh) return
      const material = new MeshBasicMaterial({
        color: HALO_COLOR,
        transparent: true,
        opacity: HALO_OPACITY,
        depthWrite: false,
        depthTest: false,
      })
      mesh.material = material
      mesh.renderOrder = 998
      materials.push(material)
    })
    // Grown about the part's own origin, which `buildDeviceMesh` puts at the device's position.
    built.group.scale.multiplyScalar(HALO_SCALE)
    built.group.name = 'build.halo'
    this.playfield.add(built.group)
    this.haloDrawn = { id: deviceId, device, group: built.group, geometries: built.geometries, materials }
  }

  /** Draw one device into the devices group and remember what was drawn. */
  private draw(device: MachineDevice, loaded: LoadedMachine): void {
    const illustrated = packagedIllustration(this.definition.machineId, this.definition.machineVersion) !== undefined
    const runtime = runtimeFor(loaded, device)
    const built = withSlingAssembly(withAuthoredSpinners(withAuthoredFlippers(withAuthoredLowerAssembly(buildDeviceMesh(device, this.palette, runtime, illustrated,
      illustrated && authoredHardwareFor(this.definition.machineId)), device, this.definition, this.palette, illustrated),
      device, this.definition, this.palette, illustrated, runtime,
      this.materials.find(m => m.name === 'cabinet.brushedSteel')),
      device, this.definition, this.palette, illustrated, runtime),device,this.definition,this.palette)
    if (built.group !== undefined) this.devicesGroup?.add(built.group)
    this.remember(device, built)
  }

  /** Record one device's build, and its reason for drawing nothing when it did. */
  private remember(device: MachineDevice, built: BuiltDevice): void {
    if (built.skipped !== undefined) this.undrawn.push(built.skipped)
    this.drawn.set(device.id, {
      definition: device,
      group: built.group,
      geometries: built.geometries,
      animator: built.animator,
      skipped: built.skipped,
      rebind: built.rebind,
    })
  }

  /** Take one drawn device out of the scene and dispose what it owned. */
  private discard(id: string, entry: DrawnDevice): void {
    if (entry.group !== undefined) this.devicesGroup?.remove(entry.group)
    for (const g of entry.geometries) g.dispose()
    if (entry.skipped !== undefined) {
      const at = this.undrawn.indexOf(entry.skipped)
      if (at >= 0) this.undrawn.splice(at, 1)
    }
    this.drawn.delete(id)
  }

  /** The animators, in the definition's order, from whatever is drawn now. */
  private refreshAnimators(): void {
    const animators: DeviceAnimator[] = []
    for (const device of this.definition.devices) {
      const animator = this.drawn.get(device.id)?.animator
      if (animator !== undefined) animators.push(animator)
    }
    this.animators = animators
  }

  /**
   * A texture for an art surface, or nothing.
   *
   * The format types every art surface as an open record, so this checks for the one field
   * that makes it drawable rather than asserting a shape it has not verified. A machine whose
   * backglass block is a typo gets a plain backbox and a machine that plays, not a throw.
   *
   * And a browser that refuses a 2-D context gets the same treatment, for the reason
   * `attachLamps` already gives: the artwork is the least load-bearing thing in the scene. An
   * unpainted playfield is a machine with grey wood on it; a throw from here propagates out of
   * `MachineScene.build`, out of the `MachineView` constructor, and costs the player the entire
   * 3-D view because a picture could not be painted onto it.
   */
  private textureFor(surface: unknown, seed: number, yUp = false, printKind?: 'cabinet' | 'backglass', printMirror = false, front = false): Texture | undefined {
    const record = asRecord(surface)
    if (record === undefined || !Array.isArray(record['program'])) return undefined
    try {
      const tile = packagedIllustration(this.definition.machineId, this.definition.machineVersion)
      const dims = cabinetDimensionsFor(this.definition), length = bodyLengthFor(dims)
      const top = dims.lockdownBarHeight+length*Math.tan(dims.incline)
      const physicalAspect = yUp ? this.definition.dimensions.playfield.width/this.definition.dimensions.playfield.length
        : printKind === 'cabinet' ? front ? (dims.width-.038)/(dims.lockdownBarHeight-cabinetBottomY(dims)) : length/(top-cabinetBottomY(dims)) : (dims.width-.05)/(dims.height-top-.09)
      const { texture } = renderArtTexture(record as unknown as ArtSurface, seed, { yUp, printFront: front,
        ...(tile === undefined || (!yUp && printKind === undefined) ? {} : { illustrationTile: tile, title: this.definition.meta.title, physicalAspect, printMirror, ...(printKind === undefined ? {} : { printKind }) }) })
      this.textures.push(texture)
      return texture
    } catch (error) {
      this.undrawn.push(`art: ${error instanceof Error ? error.message : String(error)}`)
      return undefined
    }
  }
}

/** Scratch for `listenerAt`, module-level so a frame callback allocates nothing. */
const EAR = new Vector3()
const GAZE = new Vector3()

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined
}

